import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { StatCard, Select, Button, Table, Pagination, Alert, Drawer, DatePicker, type TableColumn } from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import { formatDate, formatCurrency, formatNumber, formatCredits } from '@/ui'
import { ExternalLink } from 'lucide-react'
import styles from './Payments.module.css'

interface Payment {
  id: string
  user: { id: string; email: string; name: string | null }
  type: string
  method: string
  amount: number
  creditsGranted: number | null
  status: string
  orderId: string | null
  paymentKey: string | null
  receiptUrl: string | null
  createdAt: string
}

interface PaymentsResponse {
  payments: Payment[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  summary: { totalAmount: number; count: number }
}

const TYPE_LABELS: Record<string, string> = {
  PREMIUM_UPGRADE: '프리미엄',
  AI_CREDITS: 'AI 크레딧',
  AI_CREDITS_BUNDLE: '크레딧 번들',
  INVITATION_PACKAGE: '청첩장 패키지',
  CREDITS_10: 'AI 10장',
  CREDITS_20: 'AI 20장',
  CREDITS_30: 'AI 30장',
  CREDITS_50: '크레딧 50',
  CREDITS_100: '크레딧 100',
}

export function Payments() {
  const { filters, setFilters } = useAdminFilters()
  const [searchParams] = useSearchParams()
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  // 초기 필터값을 URL search params 에서 읽음 (드릴다운 진입 지원).
  // 이후 상호작용은 useState 유지 — 기존 동작 보존.
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '')
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get('type') ?? '')
  const [startDate, setStartDate] = useState(() => searchParams.get('startDate') ?? '')
  const [endDate, setEndDate] = useState(() => searchParams.get('endDate') ?? '')

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  params.set('sortBy', filters.sortBy)
  params.set('sortOrder', filters.sortOrder)
  if (statusFilter) params.set('status', statusFilter)
  if (typeFilter) params.set('type', typeFilter)
  if (startDate) params.set('startDate', startDate)
  if (endDate) params.set('endDate', endDate)

  const { data, isLoading, error } = useApiFetch<PaymentsResponse>(
    `/api/admin/payments?${params.toString()}`
  )

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const columns: TableColumn<Payment>[] = [
    { key: 'id', label: 'ID', render: (row) => row.id.slice(0, 8), width: '80px' },
    { key: 'user', label: '유저', render: (row) => row.user.name ?? row.user.email },
    { key: 'type', label: '타입', render: (row) => <StatusBadge status={row.type} label={TYPE_LABELS[row.type] ?? row.type} />, width: '100px' },
    { key: 'method', label: '결제수단', width: '90px' },
    { key: 'amount', label: '금액', sortable: true, render: (row) => formatCurrency(row.amount), align: 'right' as const, width: '110px' },
    { key: 'creditsGranted', label: '크레딧', render: (row) => row.creditsGranted != null ? formatCredits(row.creditsGranted) : '-', align: 'right' as const, width: '80px' },
    { key: 'status', label: '상태', sortable: true, render: (row) => <StatusBadge status={row.status} />, width: '90px' },
    { key: 'orderId', label: '주문ID', render: (row) => row.orderId ? row.orderId.slice(0, 12) : '-', width: '110px' },
    { key: 'createdAt', label: '일시', sortable: true, render: (row) => formatDate(row.createdAt, 'yyyy.M.d HH:mm'), width: '130px' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.statGrid}>
        <StatCard label="총 금액" value={formatCurrency(data?.summary.totalAmount ?? 0)} />
        <StatCard label="결제 건수" value={`${formatNumber(data?.summary.count ?? 0)}건`} />
      </div>

      <div className={styles.filters}>
        <Select
          size="sm"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setFilters({ page: 1 }) }}
          options={[
            { value: '', label: '전체 상태' },
            { value: 'PENDING', label: 'PENDING' },
            { value: 'COMPLETED', label: 'COMPLETED' },
            { value: 'FAILED', label: 'FAILED' },
            { value: 'REFUNDED', label: 'REFUNDED' },
            { value: 'PARTIALLY_REFUNDED', label: 'PARTIALLY_REFUNDED' },
          ]}
        />
        <Select
          size="sm"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setFilters({ page: 1 }) }}
          options={[
            { value: '', label: '전체 타입' },
            { value: 'PREMIUM_UPGRADE', label: '프리미엄' },
            { value: 'AI_CREDITS', label: 'AI 크레딧' },
            { value: 'AI_CREDITS_BUNDLE', label: '크레딧 번들' },
          ]}
        />
        <DatePicker
          size="sm"
          value={startDate}
          onChange={(_date, formatted) => { setStartDate(formatted); setFilters({ page: 1 }) }}
        />
        <DatePicker
          size="sm"
          value={endDate}
          onChange={(_date, formatted) => { setEndDate(formatted); setFilters({ page: 1 }) }}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<Payment>
        columns={columns}
        data={data?.payments ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
        onRowClick={(row) => setSelectedPayment(row)}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page) => setFilters({ page })}
          size="sm"
        />
      )}

      <Drawer open={!!selectedPayment} onClose={() => setSelectedPayment(null)} title="결제 상세" size="md">
        {selectedPayment && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-4)' }}>
            {/* Meta rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-3)' }}>
              {[
                ['결제 ID', selectedPayment.id],
                ['유저', selectedPayment.user.name ?? selectedPayment.user.email],
                ['타입', selectedPayment.type],
                ['결제수단', selectedPayment.method],
                ['금액', formatCurrency(selectedPayment.amount)],
                ['크레딧', selectedPayment.creditsGranted != null ? formatCredits(selectedPayment.creditsGranted) : '-'],
                ['주문 ID', selectedPayment.orderId ?? '-'],
                ['결제키', selectedPayment.paymentKey ? selectedPayment.paymentKey.slice(0, 20) + '...' : '-'],
                ['일시', formatDate(selectedPayment.createdAt, 'yyyy년 M월 d일 HH:mm:ss')],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--ds-space-2) 0', borderBottom: '1px solid var(--ds-color-border)' }}>
                  <span style={{ fontSize: 'var(--ds-font-size-sm)', color: 'var(--ds-color-text-tertiary)' }}>{label}</span>
                  <span style={{ fontSize: 'var(--ds-font-size-sm)', fontWeight: 500 }}>{value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--ds-space-2) 0' }}>
                <span style={{ fontSize: 'var(--ds-font-size-sm)', color: 'var(--ds-color-text-tertiary)' }}>상태</span>
                <StatusBadge status={selectedPayment.status} />
              </div>
            </div>

            {/* Receipt link */}
            {selectedPayment.receiptUrl && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(selectedPayment!.receiptUrl!, '_blank')}
              >
                <ExternalLink size={14} /> 토스 매출전표
              </Button>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}
